/**
 * HR Engine - Production Settings
 * Composant unifié pour gérer les lignes de production et les shifts (référentiels ouvriers).
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from './ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from './ui/alert-dialog';
import { Plus, Pencil, Trash2, Factory, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

/**
 * Panneau CRUD générique pour un référentiel production (ligne ou shift).
 */
function ReferentialPanel({ title, description, icon: Icon, endpoint, itemLabel, responseKey, testidPrefix }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, item: null });
  const [form, setForm] = useState({ name: '', description: '', isActive: true });
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/${endpoint}?include_inactive=true`);
      setItems(res.data?.[responseKey] || []);
    } catch (e) {
      toast.error(`Erreur lors du chargement : ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []); // eslint-disable-line

  const openDialog = (item = null) => {
    if (item) {
      setForm({ name: item.name, description: item.description || '', isActive: item.isActive });
    } else {
      setForm({ name: '', description: '', isActive: true });
    }
    setDialog({ open: true, item });
  };

  const closeDialog = () => setDialog({ open: false, item: null });

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Le nom est requis');
      return;
    }
    setSaving(true);
    try {
      if (dialog.item) {
        await api.put(`/${endpoint}/${dialog.item.id}`, form);
        toast.success(`${itemLabel} mis à jour`);
      } else {
        await api.post(`/${endpoint}`, form);
        toast.success(`${itemLabel} créé`);
      }
      closeDialog();
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.item) return;
    try {
      await api.delete(`/${endpoint}/${deleteDialog.item.id}`);
      toast.success(`${itemLabel} supprimé`);
      setDeleteDialog({ open: false, item: null });
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Icon className="w-5 h-5" />
            {title}
          </CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
        <Button onClick={() => openDialog()} className="bg-webtech-blue hover:bg-webtech-blue-hover text-white" data-testid={`${testidPrefix}-add-btn`}>
          <Plus className="w-4 h-4 mr-2" /> Ajouter
        </Button>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nom</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>État</TableHead>
              <TableHead className="w-[120px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-slate-500 py-8">Chargement...</TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-slate-500 py-8">Aucun élément</TableCell>
              </TableRow>
            ) : (
              items.map((it) => (
                <TableRow key={it.id}>
                  <TableCell className="font-medium">{it.name}</TableCell>
                  <TableCell className="text-slate-500 text-sm">{it.description || '-'}</TableCell>
                  <TableCell>
                    <Badge className={it.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}>
                      {it.isActive ? 'Actif' : 'Inactif'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openDialog(it)} data-testid={`${testidPrefix}-edit-${it.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDeleteDialog({ open: true, item: it })}
                        data-testid={`${testidPrefix}-delete-${it.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>

      {/* Create/Edit dialog */}
      <Dialog open={dialog.open} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{dialog.item ? `Modifier ${itemLabel.toLowerCase()}` : `Nouveau ${itemLabel.toLowerCase()}`}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nom *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={itemLabel === 'Ligne de production' ? 'ex: Ligne A — Assemblage' : 'ex: Matin (6h-14h)'}
                data-testid={`${testidPrefix}-name-input`}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                placeholder="Facultatif"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id={`${testidPrefix}-active`}
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
              <Label htmlFor={`${testidPrefix}-active`} className="cursor-pointer">Actif</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving} data-testid={`${testidPrefix}-save-btn`}>
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(o) => !o && setDeleteDialog({ open: false, item: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer {deleteDialog.item?.name} ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Si l'élément est utilisé par des employés, la suppression sera bloquée.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

export default function ProductionSettings() {
  return (
    <div className="space-y-6">
      <ReferentialPanel
        title="Lignes de production"
        description="Gérez les lignes de production disponibles pour vos ouvriers"
        icon={Factory}
        endpoint="production-lines"
        itemLabel="Ligne de production"
        responseKey="productionLines"
        testidPrefix="prod-line"
      />
      <ReferentialPanel
        title="Shifts / Équipes"
        description="Définissez les équipes de travail (matin, nuit, etc.) assignables aux ouvriers"
        icon={Clock}
        endpoint="shifts"
        itemLabel="Shift"
        responseKey="shifts"
        testidPrefix="shift"
      />
    </div>
  );
}
